import { FC, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Button from "@/components/Button";
import { useGetTestResult } from "@/hooks/useEnrollments";
import { CircularProgress, Typography } from "@mui/material";
import ChipStatus from "@/components/ChipStatus";
import dayjs from "dayjs";
import { TestResultStatusEnum } from "@/domain/enrollment.enum";
import { ProofAttemptStatusEnum } from "@/domain/enrollment.interface";

type TestResultImagesModalProps = {
  open: boolean;
  onClose: () => void;
  enrollmentId: string;
  testResultId: string;
  validationType: string;
};

const TestResultImagesModal: FC<TestResultImagesModalProps> = ({
  open,
  onClose,
  enrollmentId,
  testResultId,
  validationType,
}) => {
  const theme = useAppTheme();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const { testResult, isLoadingTestResult, isErrorTestResult } =
    useGetTestResult(enrollmentId, testResultId);

  // AC01: Get images array and sort by attempt (oldest first, newest last)
  const images = useMemo(() => {
    if (
      !testResult?.proofParticipant ||
      !Array.isArray(testResult.proofParticipant)
    ) {
      return [];
    }
    // Sort by attempt number (ascending) - first image taken to the left
    // Filter out images without s3Url (failed to generate signed URL)
    // Also filter out keys that aren't signed URLs (should be signed URLs from backend)
    const allProofs = [...testResult.proofParticipant];
    const filteredProofs = allProofs.filter(
      (proof) =>
        proof.s3Url &&
        proof.s3Url.trim() !== "" &&
        (proof.s3Url.startsWith("http://") ||
          proof.s3Url.startsWith("https://")), // Only accept signed URLs, not raw keys
    );

    return filteredProofs.sort((a, b) => {
      return (a.attempt || 0) - (b.attempt || 0);
    });
  }, [testResult]);

  // AC01: Reset selected image when modal opens or images change
  useEffect(() => {
    if (open && images.length > 0) {
      setSelectedImageIndex(0);
      setImageErrors({});
    }
  }, [open, images.length]);

  // AC03: Handle thumbnail click
  const handleThumbnailClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  // AC05: Handle image load error
  const handleImageError = (index: number) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  // AC02: Get current image data
  const currentImage = images[selectedImageIndex];

  // AC05: Check if there's an error loading images
  const hasError =
    isErrorTestResult || (images.length === 0 && !isLoadingTestResult);

  // AC02: Get status for current image (use proof.status if available, otherwise fallback)
  const getImageStatus = (image: typeof currentImage) => {
    // First, check if the proof has a status field (new field)
    // Handle both enum value and string value from backend
    const proofStatus = image?.status as
      | ProofAttemptStatusEnum
      | string
      | undefined;

    if (
      proofStatus === ProofAttemptStatusEnum.FAILED ||
      proofStatus === "FAILED"
    ) {
      return TestResultStatusEnum.FAILED;
    }

    // If proof status is SUCCESS, determine based on answer
    if (
      proofStatus === ProofAttemptStatusEnum.SUCCESS ||
      proofStatus === "SUCCESS"
    ) {
      if (image?.answer !== undefined) {
        return image.answer
          ? TestResultStatusEnum.POSITIVE
          : TestResultStatusEnum.NEGATIVE;
      }
    }

    // Fallback: use answer if available (for backward compatibility)
    // BUT only if status is not explicitly FAILED
    // (if status is undefined/null, we can use answer)
    if (image?.answer !== undefined && !proofStatus) {
      return image.answer
        ? TestResultStatusEnum.POSITIVE
        : TestResultStatusEnum.NEGATIVE;
    }

    // Final fallback: use testResult status
    return testResult?.status || TestResultStatusEnum.FAILED;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title="Validation Images"
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 750,
        maxWidth: 900,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
          {isLoadingTestResult ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : hasError ? (
            // AC05: Error Loading Images
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
                color: "error.main",
              }}
            >
              <Typography variant="bodyLarge">
                Unable to load images. Please try again later.
              </Typography>
            </Box>
          ) : images.length === 0 ? (
            // AC05: No images available
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <Typography variant="bodyLarge">No images available</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
              }}
            >
              {/* AC02: Main image display with attempt info */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* AC02: Image info header */}
                {currentImage && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="titleMedium" fontWeight={600}>
                        Attempt {currentImage.attempt || selectedImageIndex + 1}
                      </Typography>
                      <Typography variant="labelSmall" color="textMain.dark">
                        Submitted:{" "}
                        {currentImage.submissionDate
                          ? dayjs(currentImage.submissionDate).format(
                              "DD MMM YYYY, hh:mm A",
                            )
                          : "-"}
                      </Typography>
                    </Box>
                    {/* AC02: Status chip */}
                    <ChipStatus
                      status={getImageStatus(currentImage)}
                      validationType={validationType}
                    />
                  </Box>
                )}

                {/* AC01: Main image display */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    minHeight: 300,
                    maxHeight: 500,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {currentImage && currentImage.s3Url ? (
                    imageErrors[selectedImageIndex] ? (
                      <Typography variant="bodyMedium" color="error">
                        Unable to load image. Please try again later.
                      </Typography>
                    ) : (
                      <img
                        src={currentImage.s3Url}
                        alt={`Validation attempt ${currentImage.attempt || selectedImageIndex + 1}`}
                        onError={() => {
                          handleImageError(selectedImageIndex);
                        }}
                        onLoad={() => {
                          // eslint-disable-next-line no-console
                          console.log("Image loaded successfully", {
                            index: selectedImageIndex,
                            attempt: currentImage.attempt,
                          });
                        }}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "360px",
                          objectFit: "contain",
                        }}
                      />
                    )
                  ) : (
                    <Typography variant="bodyMedium" color="error">
                      No image available for this attempt.
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* AC01: Thumbnails row - horizontal scrollable */}
              {images.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    padding: 1,
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                      },
                    },
                  }}
                >
                  {images.map((image, index) => (
                    <Box
                      key={index}
                      onClick={() => handleThumbnailClick(index)}
                      sx={{
                        minWidth: 80,
                        width: 80,
                        height: 80,
                        borderRadius: 1,
                        border: selectedImageIndex === index ? 2 : 1,
                        borderColor:
                          selectedImageIndex === index
                            ? theme.palette.primary.main
                            : "divider",
                        cursor: "pointer",
                        overflow: "hidden",
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          opacity: 0.8,
                        },
                      }}
                    >
                      {!imageErrors[index] ? (
                        <img
                          src={image.s3Url}
                          alt={`Thumbnail attempt ${image.attempt || index + 1}`}
                          onError={() => handleImageError(index)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          color="error"
                          fontSize="10px"
                        >
                          Error
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          {/* AC04: Close button */}
          <Button variantType="save" onClick={onClose} label="CLOSE" />
        </Box>
      }
    />
  );
};

export default TestResultImagesModal;
